import React from "react";
import {
    makeStyles,
    Typography,
    Checkbox,
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableContainer from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import TablePagination from "@material-ui/core/TablePagination";
import TablePaginationActions from "@material-ui/core/TablePagination/TablePaginationActions";
import apiHelper from "../../../util/ApiHelper";
import { useLoading } from "../../../util/LoadingProvider";
import { toast } from "react-toastify";
import { modal } from "../../../util";
import StatusFilter from "./components/StatusFilter";

const useStyles = makeStyles((theme) => ({
    content: {},
    box: {
        padding: 0,
        background: "#ffffff",
        paddingTop: "10px",
        paddingBottom: "10px",
        minHeight: "800px",
    },
    buttonGroup: {
        marginTop: "20px",
        textAlign: "right",
        "& button": {
            color: "#306F9D",
            fontSize: "13px",
            textTransform: "inherit",
        },
    },
    pagination: {
        "& .MuiTextField-root": {
            width: "35px",
            minWidth: "auto",
        },
        "& .MuiSelect-root": {
            paddingTop: "3px",
            paddingBottom: "3px",
        },
        "& input": {
            padding: "4px 0",
            marginLeft: "10px",
            marginRight: "10px",
        },
        "& button": {
            background: "white",
            color: "#cccccc",
            border: "1px solid #CCCCCC",
            minWidth: "auto",
            width: "24px",
            height: "24px",
            margin: "4px 0",
            "&.left": {
                borderRadius: "4px 0 0 4px",
                marginLeft: "10px",
            },
            "&.right": {
                borderRadius: "0 4px 4px 0",
            },
        },
    },
}));

const CAT_LABELS = {
    MEDIA: "Media",
    GUEST: "Guest",
};

const CAT_FILTERS = [
    { value: "", label: "All" },
    { value: "GUEST", label: CAT_LABELS.GUEST },
    { value: "MEDIA", label: CAT_LABELS.MEDIA },
];

const LANG_LABELS = {
    en: "En",
    tc: "Tc",
};


const COLUMNS = [
    { label: "Ref.#", key: "referenceNumber" },
    { label: "First Name", key: "firstName" },
    { label: "Last Name", key: "lastName" },
    { label: "Email", key: "email" },
    { label: "Category", key: "cat", render: (row) => CAT_LABELS[row.cat] || row.cat },
    { label: "Language", key: "lang", render: (row) => LANG_LABELS[row.lang] || row.lang },
    { label: "Passcode", key: "passcode" },
];

function InvitedAccreditationListing(props) {
    const { disabled } = props;
    const classes = useStyles();
    const { setLoading } = useLoading();

    const [data, setData] = React.useState([]);
    const [totalElements, setTotalElements] = React.useState(0);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [keyword, setKeyword] = React.useState("");
    const [queryString, setQueryString] = React.useState("");
    const [cat, setCat] = React.useState("");
    const [catCounts, setCatCounts] = React.useState({});
    const [selectedIds, setSelectedIds] = React.useState([]);

    const fetchData = React.useCallback(async () => {
        setLoading(true);
        try {
            const result = await apiHelper.hkiff.getInvitedAccreditations(page, rowsPerPage, queryString, cat);
            if (result?.data) {
                setData(result.data.content || []);
                setTotalElements(result.data.totalElements || 0);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, queryString, cat, setLoading]);

    React.useEffect(() => {
        fetchData();
    }, [fetchData]);

    /**
     * Load the record count shown beside each category filter.
     * The list API is server-paginated, so each category is requested with
     * size=1 and only its totalElements is read. The active keyword applies.
     */
    const fetchCatCounts = React.useCallback(async () => {
        try {
            const results = await Promise.all(
                CAT_FILTERS.map((filter) =>
                    apiHelper.hkiff.getInvitedAccreditations(0, 1, queryString, filter.value)
                )
            );
            const counts = {};
            CAT_FILTERS.forEach((filter, index) => {
                counts[filter.value] = results[index]?.data?.totalElements || 0;
            });
            setCatCounts(counts);
        } catch (err) {
            console.log(err);
        }
    }, [queryString]);

    React.useEffect(() => {
        fetchCatCounts();
    }, [fetchCatCounts]);

    const handleChangePage = (_, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const triggerSearch = () => {
        setQueryString(keyword);
        setPage(0);
    };

    /**
     * Switch the category tab; the list re-fetches with `cat` from the server.
     * Selection is cleared because the selected rows may not exist in the new category.
     */
    const handleChangeCat = (newCat) => () => {
        setCat(newCat);
        setPage(0);
        setSelectedIds([]);
    };

    const toggleSelect = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        const allIds = data.map((r) => r.id);
        setSelectedIds((prev) =>
            prev.length === allIds.length ? [] : allIds
        );
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        const decision = await modal.fire({
            title: "Delete Records",
            text: `Are you sure to delete ${selectedIds.length} record(s)?`,
            showCancelButton: true,
            confirmButtonText: "Delete",
        });
        if (!decision.isConfirmed) return;
        setLoading(true);
        try {
            await apiHelper.hkiff.bulkDeleteInvitedAccreditations(selectedIds);
            toast.dark(`${selectedIds.length} record(s) deleted.`);
            setSelectedIds([]);
            fetchData();
            fetchCatCounts();
        } catch (err) {
            console.log(err);
            toast.error("Failed to delete records.");
        } finally {
            setLoading(false);
        }
    };

    const importFileRef = React.useRef();

    const handleImportClick = () => {
        importFileRef.current.value = "";
        importFileRef.current.click();
    };

    const handleSendEmail = async (row) => {
        try {
            await apiHelper.hkiff.sendInvitedAccreditationEmail(row.id);
            toast.dark("Passcode email sent.");
        } catch (err) {
            console.log(err);
            toast.error("Failed to send email.");
        }
    };

    const handleImportFile = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setLoading(true);
        try {
            const result = await apiHelper.hkiff.importInvitedAccreditations(fd);
            if (result?.data?.code === 0) {
                toast.dark("Import successful.");
                fetchData();
                fetchCatCounts();
            } else {
                let msg = "Import failed.";
                if (result?.data?.errors && typeof result.data.errors === "object") {
                    msg = "Import errors:\n" + Object.entries(result.data.errors)
                        .map(([row, err]) => `Row ${row}: ${err}`)
                        .join("\n");
                } else if (result?.data?.message) {
                    msg = result.data.message;
                }
                toast.error(msg);
            }
        } catch (err) {
            console.log(err);
            toast.error("Import failed.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={classes.content}>
            <Typography variant="h4" gutterBottom>
                Accreditation &gt; Invited Accreditation
            </Typography>

            <Box className={classes.box}>
                <StatusFilter aria-label="invited accreditation category filter">
                    {CAT_FILTERS.map((filter) => (
                        <li key={filter.value}>
                            <button
                                className={cat === filter.value ? "active" : ""}
                                type="button"
                                onClick={handleChangeCat(filter.value)}
                            >
                                <Typography variant="p">
                                    {filter.label} ({catCounts[filter.value] ?? 0})
                                </Typography>
                            </button>
                        </li>
                    ))}
                </StatusFilter>
                <Grid
                    container
                    justify="flex-end"
                    alignItems="center"
                    style={{ padding: "0 25px 10px" }}
                >
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Button
                            variant="contained"
                            style={{ background: "#d32f2f", color: "#fff" }}
                            disabled={disabled || selectedIds.length === 0}
                            onClick={handleBulkDelete}
                        >
                            Delete ({selectedIds.length})
                        </Button>
                        <input
                            ref={importFileRef}
                            type="file"
                            accept=".xlsx,.xls"
                            hidden
                            onChange={handleImportFile}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={disabled}
                            onClick={handleImportClick}
                        >
                            Import from .xlsx
                        </Button>
                        <a
                            href="/sample/hkiff-invited-accreditation-import-sample.xlsx"
                            download="hkiff-invited-accreditation-import-sample.xlsx"
                            style={{ fontSize: "12px", color: "#306F9D", textDecoration: "underline" }}
                        >
                            download sample excel
                        </a>
                        <TextField
                            label="Search"
                            variant="outlined"
                            margin="dense"
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            onKeyPress={(e) => e.key === "Enter" && triggerSearch()}
                        />
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={triggerSearch}
                        >
                            Search
                        </Button>
                    </Box>
                </Grid>

                <Grid container style={{ padding: "0 25px" }}>
                    <TableContainer
                        component={Paper}
                        style={{ boxShadow: "none", border: "1px solid #cccccc" }}
                    >
                        <fieldset disabled={disabled} className="fieldset">
                            <Table aria-label="invited accreditation table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" style={{ height: 48 }}>
                                            <Checkbox
                                                indeterminate={selectedIds.length > 0 && selectedIds.length < data.length}
                                                checked={data.length > 0 && selectedIds.length === data.length}
                                                onChange={toggleSelectAll}
                                            />
                                        </TableCell>
                                        {COLUMNS.map((col) => (
                                            <TableCell
                                                key={col.key}
                                                style={{ paddingTop: 6, paddingBottom: 6, height: 48, fontWeight: 600 }}
                                            >
                                                {col.label}
                                            </TableCell>
                                        ))}
                                        <TableCell style={{ paddingTop: 6, paddingBottom: 6, height: 48, fontWeight: 600 }}>
                                            Action
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={COLUMNS.length + 2} align="center" style={{ color: "#999" }}>
                                                No records found.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        data.map((row) => (
                                            <TableRow key={row.id} selected={selectedIds.includes(row.id)}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedIds.includes(row.id)}
                                                        onChange={() => toggleSelect(row.id)}
                                                    />
                                                </TableCell>
                                                {COLUMNS.map((col) => (
                                                    <TableCell
                                                        key={col.key}
                                                        style={{ paddingTop: 6, paddingBottom: 6, height: 48 }}
                                                    >
                                                        {col.render ? col.render(row) : row[col.key]}
                                                    </TableCell>
                                                ))}
                                                <TableCell style={{ paddingTop: 6, paddingBottom: 6, height: 48 }}>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        color="primary"
                                                        disabled={!row.passcode || row.status !== 1}
                                                        onClick={() => handleSendEmail(row)}
                                                    >
                                                        Send Email
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </fieldset>
                    </TableContainer>
                </Grid>

                <Grid container justify="flex-end" style={{ paddingRight: 25, marginTop: 15 }}>
                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[5, 10, 25]}
                        count={totalElements}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        SelectProps={{
                            inputProps: { "aria-label": "rows per page" },
                            native: true,
                            style: {
                                background: "white",
                                border: "1px solid #CCCCCC",
                                borderRadius: "4px",
                            },
                        }}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                        ActionsComponent={TablePaginationActions}
                        labelRowsPerPage="Show rows"
                    />
                </Grid>
            </Box>
        </div>
    );
}

export default InvitedAccreditationListing;
